import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MessageCircle, X, Send, ArrowUpRight, User } from 'lucide-react'
import { cn } from '@/utils/cn'
import { multichannelApi, friendlyVisitorLabel, type ChatMessage } from '@/api/multichannel'
import { useSupportChat, type LiveMessage } from '@/hooks/useSupportChat'
import { MessageStatusIcon } from '@/components/common/MessageStatusIcon'

const POSITION_KEY = 'multiaccess-support-bubble-pos'
const BUBBLE_SIZE = 56
const MARGIN = 16

interface Position { x: number; y: number }

function clampPosition(pos: Position): Position {
  const maxX = window.innerWidth - BUBBLE_SIZE - MARGIN
  const maxY = window.innerHeight - BUBBLE_SIZE - MARGIN
  return { x: Math.min(Math.max(pos.x, MARGIN), Math.max(maxX, MARGIN)), y: Math.min(Math.max(pos.y, MARGIN), Math.max(maxY, MARGIN)) }
}

function defaultPosition(): Position {
  return { x: window.innerWidth - BUBBLE_SIZE - MARGIN, y: window.innerHeight - BUBBLE_SIZE - MARGIN }
}

function loadPosition(): Position {
  try {
    const stored = localStorage.getItem(POSITION_KEY)
    if (stored) return clampPosition(JSON.parse(stored))
  } catch { /* ignore invalid stored position */ }
  return defaultPosition()
}

function timeOf(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export default function SupportChatBubble() {
  const navigate = useNavigate()
  const { liveByChat, sendMessage, markSeen } = useSupportChat()
  const [open, setOpen] = useState(false)
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [position, setPosition] = useState<Position>(loadPosition)
  const draggingRef = useRef(false)
  const movedRef = useRef(false)
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Keep the bubble on-screen across window resizes.
  useEffect(() => {
    const onResize = () => setPosition(p => clampPosition(p))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const { data: sessions = [] } = useQuery({
    queryKey: ['multichannel-sessions', 'open'],
    queryFn: () => multichannelApi.listSessions(['open', 'pending']),
    refetchInterval: 15_000,
  })

  const totalUnread = sessions.reduce((sum, s) => sum + (s.unread_count ?? 0), 0)
  const selected = sessions.find(s => s.chat_id === selectedChatId) ?? null

  // Default to the most recently active conversation when opening, or when
  // the previously-selected one disappears (e.g. resolved elsewhere).
  useEffect(() => {
    if (!open) return
    if (selectedChatId && sessions.some(s => s.chat_id === selectedChatId)) return
    setSelectedChatId(sessions[0]?.chat_id ?? null)
  }, [open, sessions, selectedChatId])

  const { data: history = [] } = useQuery({
    queryKey: ['multichannel-messages', selectedChatId],
    queryFn: () => multichannelApi.getMessages(selectedChatId!),
    enabled: !!selectedChatId,
  })

  const liveMessages = selectedChatId ? liveByChat[selectedChatId] ?? [] : []
  const combinedMessages = useMemo(() => {
    const fromHistory: LiveMessage[] = history.map((m: ChatMessage) => ({
      id: m.id, chatId: m.chat_id, senderId: m.sender_id, content: m.content, sentAt: m.sent_at,
      mine: m.sender_id !== selected?.customer_id,
      status: m.seen ? 'seen' : 'delivered',
    }))
    return [...fromHistory, ...liveMessages]
  }, [history, liveMessages, selected])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [combinedMessages])

  useEffect(() => {
    if (open && selectedChatId) markSeen(selectedChatId)
  }, [open, selectedChatId, liveMessages.length, markSeen])

  // Dragging — only the collapsed bubble is draggable; the expanded panel
  // stays anchored to wherever the bubble currently sits.
  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true
    movedRef.current = false
    dragStartRef.current = { x: e.clientX, y: e.clientY, posX: position.x, posY: position.y }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || !dragStartRef.current) return
    const dx = e.clientX - dragStartRef.current.x
    const dy = e.clientY - dragStartRef.current.y
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true
    setPosition(clampPosition({ x: dragStartRef.current.posX + dx, y: dragStartRef.current.posY + dy }))
  }
  const onPointerUp = () => {
    draggingRef.current = false
    if (movedRef.current) {
      setPosition(p => { localStorage.setItem(POSITION_KEY, JSON.stringify(p)); return p })
    } else {
      setOpen(v => !v)
    }
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!draft.trim() || !selectedChatId) return
    sendMessage(selectedChatId, draft)
    setDraft('')
  }

  // Panel anchors above-left of the bubble, clamped so it never runs off
  // the top/left edge regardless of where the bubble's been dragged to.
  const panelStyle: React.CSSProperties = {
    left: Math.min(Math.max(position.x - 260 + BUBBLE_SIZE, MARGIN), window.innerWidth - 360 - MARGIN),
    top: Math.max(position.y - 480, MARGIN),
  }

  return (
    <>
      {open && (
        <div
          className="fixed z-50 w-90 h-[30rem] rounded-2xl border border-border bg-bg-elevated shadow-2xl flex flex-col overflow-hidden"
          style={{ ...panelStyle, width: 360 }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-bg-subtle flex-shrink-0">
            <p className="text-sm font-semibold text-text-primary flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4 text-brand-500" /> Live Chat
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => navigate('/tickets/live-chat')} title="Open full inbox"
                className="p-1.5 rounded-lg hover:bg-bg-muted text-text-tertiary hover:text-text-primary transition-colors">
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-bg-muted text-text-tertiary hover:text-text-primary transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {sessions.length > 1 && (
            <div className="flex gap-1.5 px-3 py-2 border-b border-border overflow-x-auto flex-shrink-0">
              {sessions.map(s => (
                <button key={s.chat_id} onClick={() => setSelectedChatId(s.chat_id)}
                  className={cn('px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1',
                    selectedChatId === s.chat_id ? 'bg-brand-500 text-white' : 'bg-bg-subtle text-text-secondary hover:bg-bg-muted')}>
                  {s.customer_name || friendlyVisitorLabel(s.customer_id)}
                  {!!s.unread_count && (
                    <span className={cn('min-w-[14px] h-[14px] px-1 rounded-full text-[9px] font-semibold flex items-center justify-center',
                      selectedChatId === s.chat_id ? 'bg-white/25' : 'bg-brand-500 text-white')}>
                      {s.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-2">
              <MessageCircle className="w-8 h-8 text-text-tertiary" />
              <p className="text-sm font-medium text-text-secondary">No active conversations</p>
              <p className="text-xs text-text-tertiary">New chats will show up here in real time.</p>
            </div>
          ) : (
            <>
              <div className="px-3.5 py-2 border-b border-border flex items-center gap-2 flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-bg-subtle flex items-center justify-center flex-shrink-0">
                  <User className="w-3 h-3 text-text-tertiary" />
                </div>
                <p className="text-xs font-medium text-text-primary truncate">{selected.customer_name || friendlyVisitorLabel(selected.customer_id)}</p>
              </div>
              <div ref={listRef} className="flex-1 overflow-y-auto p-3.5 space-y-2 bg-bg-subtle">
                {combinedMessages.map(m => (
                  <div key={m.id} className={cn('max-w-[80%] px-3 py-1.5 rounded-2xl text-xs leading-relaxed', m.mine
                    ? 'ml-auto bg-brand-500 text-white rounded-br-md'
                    : 'bg-bg-elevated border border-border text-text-primary rounded-bl-md')}>
                    {m.content}
                    <span className={cn('flex items-center gap-1 mt-0.5', m.mine ? 'text-white/70' : 'text-text-tertiary')}>
                      <span className="text-[9px]">{timeOf(m.sentAt)}</span>
                      {m.mine && <MessageStatusIcon status={m.status} />}
                    </span>
                  </div>
                ))}
              </div>
              <form onSubmit={submit} className="flex items-center gap-2 p-2.5 border-t border-border flex-shrink-0">
                <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Reply…"
                  className="flex-1 h-9 px-3 text-xs rounded-lg border border-border bg-bg-elevated focus:outline-none focus:ring-1 focus:ring-brand-500/30 text-text-primary placeholder:text-text-tertiary" />
                <button type="submit" disabled={!draft.trim()}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-40 flex-shrink-0 transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="fixed z-50 w-14 h-14 rounded-full shadow-lg bg-brand-500 hover:bg-brand-600 text-white flex items-center justify-center transition-colors cursor-grab active:cursor-grabbing touch-none"
        style={{ left: position.x, top: position.y }}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
        {!open && totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-status-error text-white text-[10px] font-semibold flex items-center justify-center border-2 border-bg-base">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>
    </>
  )
}
